import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useCurrency } from '../../hooks/useCurrency';
import { getDisplayPrice, getSecondaryPrice } from '../../lib/currency';

const adguardFeatures = [
  {
    icon: 'ri-shield-check-line',
    title: 'Cifrado de Nivel Militar',
    description: 'Protocolo propio AdGuard VPN con cifrado AES-256 para máxima seguridad en todas tus conexiones.',
  },
  {
    icon: 'ri-speed-line',
    title: 'Velocidad Sin Límites',
    description: 'Servidores optimizados en más de 30.000 ubicaciones para una navegación rápida y sin interrupciones.',
  },
  {
    icon: 'ri-eye-off-line',
    title: 'Política Estricta No-Logs',
    description: 'No almacenamos ni rastreamos tu actividad en línea. Tu privacidad es nuestra prioridad.',
  },
  {
    icon: 'ri-device-line',
    title: 'Hasta 10 Dispositivos',
    description: 'Protege todos tus dispositivos simultáneamente: Windows, Mac, Android, iOS y más.',
  },
  {
    icon: 'ri-advertisement-line',
    title: 'Bloqueo de Anuncios Integrado',
    description: 'Combina VPN con el potente bloqueador de anuncios de AdGuard para una experiencia limpia.',
  },
  {
    icon: 'ri-global-line',
    title: '30.000+ Ubicaciones',
    description: 'Servidores en más de 30.000 ubicaciones alrededor del mundo para acceder a contenido sin restricciones.',
  },
];

const plans = [
  {
    name: '1 Mes',
    subtitle: '1 Dispositivo',
    price: 30500,
    oldPrice: 45000,
    discount: 32,
    popular: false,
  },
  {
    name: '1 Año',
    subtitle: '5 Dispositivos',
    price: 18500,
    oldPrice: 45000,
    discount: 59,
    priceNote: '/mes',
    popular: false,
  },
  {
    name: '2 Años',
    subtitle: '10 Dispositivos',
    price: 12890,
    oldPrice: 45000,
    discount: 71,
    priceNote: '/mes',
    popular: true,
  },
];

const comingSoon = [
  {
    name: 'NordVPN',
    color: '#4687FF',
    icon: 'ri-shield-line',
  },
  {
    name: 'ExpressVPN',
    color: '#DA3940',
    icon: 'ri-rocket-line',
  },
  {
    name: 'Surfshark',
    color: '#1ECAD3',
    icon: 'ri-water-flash-line',
  },
  {
    name: 'CyberGhost',
    color: '#F5C518',
    icon: 'ri-ghost-line',
  },
];

const formatPrice = (price: number) => {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
};

export default function VPNComparisonPage() {
  const { currency } = useCurrency();

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-emerald-900 via-teal-800 to-cyan-900 pt-20 sm:pt-24 pb-14 sm:pb-20 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://public.readdy.ai/ai/img_res/cd3c9f22da7012a7d3bf80466948f6e2.jpg"
            alt="VPN Network"
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/40"></div>

        <div className="container mx-auto px-4 relative z-10 text-center w-full">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-3 sm:px-4 py-1.5 sm:py-2 rounded-full mb-4 sm:mb-6">
            <i className="ri-verified-badge-line text-emerald-400"></i>
            <span className="text-white/90 text-xs sm:text-sm font-medium">Proveedor VPN Recomendado</span>
          </div>

          <div className="flex justify-center mb-4 sm:mb-6">
            <div className="w-16 h-16 sm:w-24 sm:h-24 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center p-1.5 sm:p-2">
              <img
                src="https://static.readdy.ai/image/18682db60553df15ba50b947d4252b50/6b8688c129017d7e175bddd371672b98.png"
                alt="AdGuard VPN"
                className="w-full h-full object-contain"
              />
            </div>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-3 sm:mb-4 max-w-3xl mx-auto">
            AdGuard VPN
          </h1>
          <p className="text-base sm:text-xl text-white/80 mb-6 sm:mb-8 max-w-2xl mx-auto px-2">
            Privacidad real, velocidad sin límites y protección total para todos tus dispositivos
          </p>

          <div className="flex flex-wrap gap-3 sm:gap-6 justify-center mt-6 sm:mt-8">
            <div className="flex items-center gap-1.5 sm:gap-2 text-white/80">
              <i className="ri-shield-check-line text-emerald-400 text-base sm:text-xl"></i>
              <span className="text-xs sm:text-sm">Cifrado AES-256</span>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2 text-white/80">
              <i className="ri-speed-line text-emerald-400 text-base sm:text-xl"></i>
              <span className="text-xs sm:text-sm">Sin límite de velocidad</span>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2 text-white/80">
              <i className="ri-eye-off-line text-emerald-400 text-base sm:text-xl"></i>
              <span className="text-xs sm:text-sm">Política No-Logs</span>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2 text-white/80">
              <i className="ri-global-line text-emerald-400 text-base sm:text-xl"></i>
              <span className="text-xs sm:text-sm">30.000+ Ubicaciones</span>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Plans */}
      <section className="py-10 sm:py-16 -mt-6 sm:-mt-8 relative z-10">
        <div className="container mx-auto px-3 sm:px-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 max-w-4xl mx-auto">
            {plans.map((plan, index) => {
              const display = getDisplayPrice(plan.price, plan.oldPrice, currency);
              const secondary = getSecondaryPrice(plan.price, plan.oldPrice, currency);
              return (
                <div
                  key={index}
                  className={`bg-white rounded-2xl shadow-lg overflow-hidden transition-all hover:shadow-xl ${
                    plan.popular ? 'ring-2 ring-emerald-500 relative sm:scale-105' : ''
                  }`}
                >
                  {plan.popular && (
                    <div className="bg-emerald-500 text-white text-center py-1.5 text-xs font-bold tracking-wider">
                      <i className="ri-star-fill mr-1"></i>
                      MEJOR OPCIÓN
                    </div>
                  )}
                  <div className="p-4 sm:p-6 text-center">
                    <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-0.5 sm:mb-1">{plan.name}</h3>
                    <p className="text-xs sm:text-sm text-gray-500 mb-3 sm:mb-4">{plan.subtitle}</p>

                    <div className="text-gray-400 text-xs sm:text-sm line-through">{display.oldMain}</div>
                    <div className="flex items-baseline justify-center gap-1 mb-1">
                      <span className="text-2xl sm:text-3xl font-bold text-gray-900">{display.main}</span>
                      {plan.priceNote && (
                        <span className="text-gray-500 text-xs sm:text-sm">{plan.priceNote}</span>
                      )}
                    </div>
                    <div className="flex items-center justify-center gap-2 mb-1">
                      <span className="text-[10px] sm:text-xs text-emerald-600 font-medium">≈ {secondary.current}</span>
                      {secondary.old && (
                        <span className="text-[10px] sm:text-xs text-gray-400 line-through">{secondary.old}</span>
                      )}
                    </div>
                    <div className="inline-block bg-emerald-100 text-emerald-700 text-[10px] sm:text-xs font-bold px-2 sm:px-3 py-0.5 sm:py-1 rounded-full mb-4 sm:mb-5">
                      AHORRA {plan.discount}%
                    </div>

                    <Link
                      to="/vpn"
                      className={`block w-full py-2.5 sm:py-3 rounded-full text-center font-semibold transition-all cursor-pointer whitespace-nowrap text-sm ${
                        plan.popular
                          ? 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-md shadow-emerald-200'
                          : 'bg-gray-900 text-white hover:bg-gray-800'
                      }`}
                    >
                      VER OFERTA <i className="ri-arrow-right-line ml-1"></i>
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-10 sm:py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-1 sm:mb-2">¿Por Qué AdGuard VPN?</h2>
          <p className="text-sm sm:text-base text-gray-600 text-center mb-8 sm:mb-12">Todo lo que necesitas para navegar seguro y sin restricciones</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8 max-w-5xl mx-auto">
            {adguardFeatures.map((feature, index) => (
              <div key={index} className="flex gap-3 sm:gap-4 p-4 sm:p-5 rounded-2xl hover:bg-gray-50 transition-colors">
                <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-emerald-100 text-emerald-600 rounded-xl flex-shrink-0">
                  <i className={`${feature.icon} text-xl sm:text-2xl`}></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-0.5 sm:mb-1 text-sm sm:text-base">{feature.title}</h3>
                  <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compatibility Section */}
      <section className="py-10 sm:py-16 bg-gray-50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1 sm:mb-2">Compatible con Todos tus Dispositivos</h2>
          <p className="text-sm sm:text-base text-gray-600 mb-8 sm:mb-12">Instala AdGuard VPN en cualquier plataforma</p>

          <div className="grid grid-cols-3 sm:flex sm:flex-wrap justify-center gap-4 sm:gap-8 max-w-3xl mx-auto">
            {[
              { icon: 'ri-windows-line', name: 'Windows' },
              { icon: 'ri-apple-line', name: 'macOS' },
              { icon: 'ri-android-line', name: 'Android' },
              { icon: 'ri-smartphone-line', name: 'iOS' },
              { icon: 'ri-chrome-line', name: 'Chrome' },
              { icon: 'ri-firefox-line', name: 'Firefox' },
            ].map((platform, index) => (
              <div key={index} className="flex flex-col items-center gap-1.5 sm:gap-2 sm:w-24">
                <div className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center bg-white rounded-xl sm:rounded-2xl shadow-sm">
                  <i className={`${platform.icon} text-2xl sm:text-3xl text-gray-700`}></i>
                </div>
                <span className="text-xs sm:text-sm text-gray-600 font-medium">{platform.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Coming Soon Section */}
      <section className="py-10 sm:py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-1 sm:mb-2">Otros Proveedores VPN</h2>
          <p className="text-sm sm:text-base text-gray-600 text-center mb-8 sm:mb-12">Estamos trabajando para traerte más opciones muy pronto</p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6 max-w-3xl mx-auto">
            {comingSoon.map((vpn, index) => (
              <div
                key={index}
                className="relative bg-gray-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 text-center opacity-60 group"
              >
                <div className="absolute inset-0 bg-gray-50/80 backdrop-blur-[1px] rounded-xl sm:rounded-2xl z-10 flex items-center justify-center">
                  <span className="bg-gray-200 text-gray-600 text-[10px] sm:text-xs font-bold px-2 sm:px-4 py-1 sm:py-1.5 rounded-full tracking-wider">
                    PRÓXIMAMENTE
                  </span>
                </div>
                <div
                  className="w-10 h-10 sm:w-14 sm:h-14 rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-3"
                  style={{ backgroundColor: `${vpn.color}15` }}
                >
                  <i className={`${vpn.icon} text-xl sm:text-2xl`} style={{ color: vpn.color }}></i>
                </div>
                <h3 className="font-semibold text-gray-700 text-xs sm:text-base">{vpn.name}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-12 sm:py-20 overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Cyber%20security%20digital%20protection%20concept%20dark%20teal%20green%20gradient%20background%20abstract%20network%20connections%20futuristic%20technology%20high%20quality&width=1920&height=500&seq=30&orientation=landscape"
          alt="CTA Background"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/90 to-teal-900/90"></div>

        <div className="container mx-auto px-4 relative z-10 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 sm:mb-4">
            Protege tu Privacidad con AdGuard VPN
          </h2>
          <p className="text-white/80 text-sm sm:text-lg mb-6 sm:mb-8 max-w-2xl mx-auto">
            Hasta 71% de descuento en planes de 2 años. Navega seguro desde hoy.
          </p>
          <Link
            to="/vpn"
            className="inline-flex items-center gap-2 bg-white text-emerald-700 font-semibold px-6 sm:px-8 py-3 sm:py-4 rounded-full hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap text-sm sm:text-base"
          >
            <i className="ri-shield-check-line text-lg sm:text-xl"></i>
            VER PLANES Y PRECIOS
            <i className="ri-arrow-right-up-line"></i>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
